// src/components/RegisterBox.js
import React, { useMemo, useState } from 'react';
import config from '../../config';
import './Login.css';

export default function RegisterBox({ onRegistrationSuccess }) {
  const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [error, setError] = useState(null);
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting'

  // ----- Password rule checks (live) -----
  const checks = useMemo(() => {
    const hasLen = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNum = /\d/.test(password);
    return { hasLen, hasUpper, hasLower, hasNum };
  }, [password]);

  const emailsMatch = email === confirmEmail;

  // Button enabled only when all fields valid
  const formValid =
    Boolean(email) &&
    Boolean(confirmEmail) &&
    Boolean(username.trim()) &&
    emailsMatch &&
    checks.hasLen &&
    checks.hasUpper &&
    checks.hasLower &&
    checks.hasNum &&
    termsAccepted;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting' || !formValid) return;

    setStatus('submitting');
    setError(null);

    try {
      if (!API_URL) throw new Error('REACT_APP_API_URL is not configured');

      const res = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          email: email.trim(),
          password,
        }),
      });

      if (!res.ok) {
        if (res.status === 409) {
          throw new Error('That email or username is already registered.');
        }
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Registration failed. Please try again.');
      }

      onRegistrationSuccess?.();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setStatus('idle');
    }
  };

  return (
    <form className="register-form" onSubmit={handleSubmit} noValidate>

      {/* Username */}
      <div className="form-group">
        <label htmlFor="reg-username">Username</label>
        <input
          id="reg-username"
          type="text"
          autoComplete="username"
          required
          value={username}
          disabled={status === 'submitting'}
          onChange={(e) => setUsername(e.target.value)}
        />
      </div>

      {/* Email */}
      <div className="form-group">
        <label htmlFor="reg-email">Email</label>
        <input
          id="reg-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          disabled={status === 'submitting'}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      {/* Confirm Email */}
      <div className="form-group">
        <label htmlFor="reg-email-confirm">Confirm Email</label>
        <input
          id="reg-email-confirm"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={confirmEmail}
          disabled={status === 'submitting'}
          onChange={(e) => setConfirmEmail(e.target.value)}
          aria-invalid={confirmEmail.length > 0 && !emailsMatch}
        />
        {!emailsMatch && confirmEmail.length > 0 && (
          <small className="input-warning">Emails do not match</small>
        )}
      </div>

      {/* Password */}
      <div className="form-group">
        <label htmlFor="reg-password">Password</label>
        <div className="password-field">
          <input
            id="reg-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            minLength={8}
            pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}"
            title="At least 8 characters, including uppercase, lowercase, and a number."
            value={password}
            disabled={status === 'submitting'}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            disabled={status === 'submitting'}
          >
            {showPassword ? '◉' : '○'}
          </button>
        </div>

        {/* Live requirement checklist */}
        <ul className="req-list" aria-live="polite">
          <li className={`req-item ${checks.hasLen ? 'valid' : ''}`}>8+ characters</li>
          <li className={`req-item ${checks.hasUpper ? 'valid' : ''}`}>At least 1 uppercase</li>
          <li className={`req-item ${checks.hasLower ? 'valid' : ''}`}>At least 1 lowercase</li>
          <li className={`req-item ${checks.hasNum ? 'valid' : ''}`}>At least 1 number</li>
        </ul>
      </div>

      {/* Terms of Service Checkbox */}
      <div style={{ marginTop: '0.5rem' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            disabled={status === 'submitting'}
            required
            style={{ marginTop: '0.2rem', marginRight: '0.5rem', cursor: 'pointer', flexShrink: 0 }}
          />
          <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>
            I agree to the{' '}
            <a
              href={config.siteTermsURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Terms
            </a>
            {' '}and{' '}
            <a
              href={config.sitePrivacyURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
          </span>
        </label>
      </div>

      {error && <p className="error-message" role="alert">{error}</p>}

      <button
        className="submit-button"
        disabled={!formValid || status === 'submitting'}
        aria-disabled={!formValid || status === 'submitting'}
      >
        {status === 'submitting' ? 'Creating Account...' : 'Create Account'}
      </button>
    </form>
  );
}
